import type { AgentTreeNodeDto } from '@inference.engine/api-client/web';
import { collectAllTools } from '../ui/AgentTree';
import { getToolUiSchema, getFieldUiSchemas } from '../ui/ToolConfigPage/toolCustomWidgets';

export interface FieldMapping {
  toolId: string;
  agentId: string;
}

export interface MergedField {
  key: string;
  schema: Record<string, unknown>;
  mappings: FieldMapping[];
  currentValue: unknown;
  configuredCount: number;
  totalCount: number;
}

export interface MergedSchema {
  fields: MergedField[];
  jsonSchema: Record<string, unknown>;
  currentValues: Record<string, unknown>;
}

/**
 * Walks the agent tree, collects all configurable tools, and merges their
 * schemas by deduplicating fields by key name.
 */
export function mergeToolSchemas(tree: AgentTreeNodeDto): MergedSchema {
  const allTools = collectAllTools(tree).filter((t) => t.isConfigurable);
  const fieldMap = new Map<string, MergedField>();

  for (const tool of allTools) {
    if (!tool.jsonSchema) continue;
    const properties = (tool.jsonSchema as { properties?: Record<string, unknown> }).properties;
    if (!properties) continue;

    for (const [key, fieldSchema] of Object.entries(properties)) {
      const hasValue = tool.currentData != null && key in tool.currentData;
      const existing = fieldMap.get(key);

      if (existing) {
        existing.mappings.push({ toolId: tool.toolId, agentId: tool.agentId });
        existing.totalCount++;
        if (hasValue) {
          existing.configuredCount++;
          if (existing.currentValue == null) {
            existing.currentValue = tool.currentData![key];
          }
        }
      } else {
        fieldMap.set(key, {
          key,
          schema: fieldSchema as Record<string, unknown>,
          mappings: [{ toolId: tool.toolId, agentId: tool.agentId }],
          currentValue: hasValue ? tool.currentData![key] : null,
          configuredCount: hasValue ? 1 : 0,
          totalCount: 1,
        });
      }
    }
  }

  const fields = Array.from(fieldMap.values());

  const jsonSchema: Record<string, unknown> = {
    type: 'object',
    properties: Object.fromEntries(fields.map((f) => [f.key, f.schema])),
    required: fields.map((f) => f.key),
  };

  const currentValues: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.currentValue != null) {
      currentValues[field.key] = field.currentValue;
    }
  }

  return { fields, jsonSchema, currentValues };
}

/**
 * Builds a merged RJSF UI schema for the merged form fields.
 * Uses two strategies:
 * 1. Field-name matching — some field names always use specific widgets regardless of tool
 * 2. Tool-ID matching — falls back to per-tool UI schemas for remaining fields
 */
export function mergeToolUiSchemas(fields: MergedField[]): Record<string, unknown> {
  const fieldUiSchemas = getFieldUiSchemas();
  const merged: Record<string, unknown> = {};

  // Strategy 1: match by field name
  for (const field of fields) {
    if (field.key in fieldUiSchemas) {
      merged[field.key] = fieldUiSchemas[field.key];
    }
  }

  // Strategy 2: match by tool ID for any fields not yet covered
  const seenToolIds = new Set<string>();
  for (const field of fields) {
    if (merged[field.key]) continue;
    for (const mapping of field.mappings) {
      seenToolIds.add(mapping.toolId);
    }
  }
  for (const toolId of seenToolIds) {
    const schema = getToolUiSchema(toolId);
    for (const [key, value] of Object.entries(schema)) {
      if (!merged[key]) {
        merged[key] = value;
      }
    }
  }

  return merged;
}

/**
 * Given merged form data and merged fields, constructs the batch save payload.
 */
export function buildBatchSavePayload(
  formData: Record<string, unknown>,
  fields: MergedField[],
  organizationId: string,
  workspaceId?: string,
): Array<{
  toolId: string;
  agentId: string;
  organizationId: string;
  workspaceId?: string;
  data: Record<string, unknown>;
}> {
  const groups = new Map<string, { toolId: string; agentId: string; fields: string[] }>();

  for (const field of fields) {
    for (const mapping of field.mappings) {
      const key = `${mapping.toolId}:${mapping.agentId}`;
      if (!groups.has(key)) {
        groups.set(key, { toolId: mapping.toolId, agentId: mapping.agentId, fields: [] });
      }
      groups.get(key)!.fields.push(field.key);
    }
  }

  return Array.from(groups.values())
    .map((group) => {
      const data: Record<string, unknown> = {};
      for (const fieldKey of group.fields) {
        if (fieldKey in formData) {
          data[fieldKey] = formData[fieldKey];
        }
      }
      return {
        toolId: group.toolId,
        agentId: group.agentId,
        organizationId,
        ...(workspaceId ? { workspaceId } : {}),
        data,
      };
    })
    .filter((item) => Object.keys(item.data).length > 0);
}
